import React, { useState } from "react";
import styled from "styled-components";
import {
  BlankImage,
  FireIcon,
  LeafIcon
} from "../../../shared/icons";
import { Grid2 as Grid, Skeleton, Box } from "@mui/material";
import FilterDrawer from "./FilterDrawer";
import Loader from "../../../components/shared-components/Loader";
import { screenSizes } from "../../../styles/theme";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { getValidImageUrl } from "../../../utils";

const NFTsListingContainer = styled.section`
  padding: 0 80px;
  @media (max-width: ${screenSizes.L}px) {
    padding: 0 20px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const TitleContainer = styled.div`
  color: #091c00;
  h2 {
    font-family: "Manrope", sans-serif;
    font-size: 50px;
    font-weight: 700;
    letter-spacing: -2px;
    margin: 0;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 32px;
      margin-bottom: 5px;
      letter-spacing: 0;
      line-height: 40px;
    }
  }

  h5 {
    font-family: "Manrope", sans-serif;
    color: rgba(9, 28, 0, 0.8);
    font-size: 18px;
    font-weight: 400;
    margin: 0;
    @media (max-width: ${screenSizes.L}px) {
      font-size: 16px;
    }
  }
`;

const Card = styled.div`
  padding: 12px;
  background-color: #fff;
  border: 1px solid rgba(9, 28, 0, 0.1);
  img {
    height: 224px;
    width: 100%;
    margin-bottom: 10px;
    background-size: cover;
    background-position: center;
    @media (max-width: ${screenSizes.L}px) {
      height: 107px;
      margin-bottom: 0px;
    }
  }
  .icon {
    margin-bottom: 2px;
  }
  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
    margin-bottom: 5px;
    display: -webkit-box; //  box layout for clamping
    -webkit-line-clamp: 1; // Limit to 2 lines
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
  }
  h6 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: rgba(9, 28, 0, 1);
  }
`;

const LoaderWrapper = styled.div`
  margin-top: 15px;
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
`;

const StyledCard = styled(Card)<{ isMobile: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "row" : "column")};
  align-items: ${(props) => (props.isMobile ? "center" : "flex-start")};
  padding: ${(props) => (props.isMobile ? "10px" : "12px")};
  gap: ${(props) => (props.isMobile ? "2px" : "0px")};
  height: ${(props) => (props.isMobile ? "127px" : "342px")};
`;

const SkeletonWrapper = styled(Box)<{ isMobile: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: ${screenSizes.L}px) {
    width: 150px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  @media (max-width: ${screenSizes.L}px) {
    height: 100%;
    margin-left: 5px;
    justify-content: space-between;
  }
`;

const ContentWrapper = styled(Box)<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  padding-left: 0px;
`;

const PriceAndScore = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Rating = styled.div<{ value: number }>`
  display: flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  min-width: 70px;
  justify-content: center;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
  }
`;

type NFTs = {
  id: string;
  nftImage: string;
  nftName: string;
  ecoScore: number;
};

interface Props {
  NFTs: NFTs[] | [];
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  getMoreNFTs?: (limit: number, pageNo: number, isLoadingMore: boolean) => void;
  isLoadingMore: boolean;
  getFilteredNFTs: (
    name: string,
    blockchain_network: string,
    rank_by: string,
    category_id: string,
    eco_category_id: string,
    limit?: number,
    pageNo?: number,
    isLoadingMore?: boolean
  ) => void;
}

const NFTsListingSection: React.FC<Props> = ({
  NFTs,
  isLoading,
  currentPage,
  totalPages,
  getMoreNFTs,
  isLoadingMore,
  getFilteredNFTs,
}) => {
  const navigate = useNavigate();
  const { name, network, impactScore, categories, ecoCategories } = useSelector(
    (state: RootState) => state.filter
  );
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleToggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      getFilteredNFTs(
        name,
        network,
        impactScore,
        categories,
        ecoCategories,
        10,
        currentPage + 1,
        true
      );
    }
  };

  const isMobile = window.innerWidth <= 768;

  const handlePrice = (networkId: string|number, price: string|number) => {
    let calculatedPrice = (networkId === 1)
    ? Number(price).toFixed(6)
    : (networkId === 295)
    ? Number(price)
    : Number(price).toFixed(6);
    return calculatedPrice;
  }

  return (
    <NFTsListingContainer>
      {/* header */}
      <HeaderContainer>
        <TitleContainer>
          <h2>Featured Digital Assets</h2>
          <h5>Discover standout digital assets available across multiple chains.</h5>
        </TitleContainer>
      </HeaderContainer>

      {/* filter drawer */}
      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={handleToggleFilter}
        getFilteredNFTs={getFilteredNFTs}
      />

      {/* cards */}
      <Grid container spacing={2}>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : NFTs?.length > 0 ? (
          NFTs?.map((item: any) => (
            <Grid size={{ xs: 12, md: 2.4 }}>
              <StyledCard
                isMobile={isMobile}
                onClick={() => navigate(`/nft-details/${item?.id}`)}
              >
                <SkeletonWrapper isMobile>
                  {!imageLoaded && (
                    <Skeleton
                      variant="rectangular"
                      width={isMobile ? 107 : 200}
                      height={isMobile ? 107 : 200}
                      animation="wave"
                    />
                  )}
                  <img
                    src={
                      getValidImageUrl(item?.nftImage, BlankImage)
                    }
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = BlankImage;
                    }}
                    alt="nft_image"
                    style={{
                      display: imageLoaded ? "block" : "none",
                      width: isMobile ? "100px" : "224px",
                      height: isMobile ? "107px" : "224px",
                    }}
                    onLoad={handleImageLoad}
                  />
                </SkeletonWrapper>
                <ContentContainer>
                  <ContentWrapper isMobile>
                    <FireIcon className="icon" />
                    <h5 title={item?.nftName} style={{ margin: "8px 0" }}>
                      {item?.nftName}
                    </h5>
                  </ContentWrapper>
                  <PriceAndScore>
                    <h6
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: 400,
                        color: "#091C00",
                      }}
                    >
                      {handlePrice(item?.networkId, item?.price)}{" "}
                      {item?.networkId === 1
                        ? "ETH"
                        : item?.networkId === 137
                        ? "POL"
                        : "HBAR"}
                    </h6>
                    <Rating value={item?.ecoScore}>
                      <LeafIcon />
                      <span>
                        {typeof item?.ecoScore === "number"
                          ? item.ecoScore.toFixed(1)
                          : "0"}
                      </span>
                    </Rating>
                  </PriceAndScore>
                </ContentContainer>
              </StyledCard>
            </Grid>
          ))
        ) : (
          <NoResults>No Results Found!</NoResults>
        )}
      </Grid>

      {isLoadingMore && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
    </NFTsListingContainer>
  );
};

export default NFTsListingSection;
